import React, { useRef, useCallback, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { VariableSizeList as List } from 'react-window'
import styled from 'styled-components'

import i18n from 'live/services/i18n'
import LiveDateService from 'live/services/date'
import ActionConfirmationInputDialog from 'live/components/actions/ui/confirmation-input-dialog'

import { useWindowResize } from '../../hooks/useWindowResize'
import { Loading, SimpleLoading } from '../../components/loading'

import ModalCreateIndex, { MappingCollectionType } from '../components/create-index-modal'
import IndexesTable from '../components/indexes-table'

import {
    bytesToSize,
    isIdIndex,
    isTimestampIndex,
    isLiveCollection,
    isRegularCollectionName,
    isLongCollectionName
} from '../../../lib/utils'
import { CollectionFilterEnum } from './collections-filter'

export type Stats = {
    type: string
    count: number
    dataSize: number
    indexSize: number
    bytesUsed: number
}

export type Index = {
    collection?: string
    key: Record<string, number>
    partialFilterExpression?: Record<string, any>
    name: string
    size?: number
    sparse: boolean
}

export type Collection = {
    name: string
    type: string
    stats: null | Stats
    indexes: Index[]
    trivialIndexes: number
    customIndexes: number
    createdIn: number
    partialResult: boolean
}
interface CollectionTableProps {
    collections: Collection[]
    readonly: boolean
    loading: boolean
    searchText: string
    collectionsNames: MappingCollectionType[]
    selectedFilter: CollectionFilterEnum
    hideTrivial: boolean
    decrementHeight: number
    deleteTable: (name: string) => void
    deleteIndex: (selectedIndex: Index) => void
    saveIndex(selectedCollections: string[], index: Index): void
}

const sizeManager = function (index: number, collections: Collection[]): number {
    const collection = collections[index]

    // padding-top + padding-bottom + title + delete + delete(top) + collection-info + collection-info(margin-top) + collection-info(bottom)
    const col1 = 15 + 15 + 49 + 17 + 2 + 35 + 15 + 8

    const indexes = collection?.indexes
    const haveIndex = indexes && indexes.length
    // padding-top + padding-bottom + collection-cache-info
    let col2 = 15 + 15 + 18

    const partialResult = collection.partialResult
    if (partialResult) {
        // padding-top + padding-bottom + loading
        col2 += 15 + 15 + 15
    }

    if (haveIndex) {
        const indexesPartial = indexes.filter(index => index.partialFilterExpression)
        const simpleIndex = indexes.length - indexesPartial.length
        // (num of index) * index card size + add button + add button(padding) + 20px more to prevent not track break
        col2 += simpleIndex * 67.5 + indexesPartial.length * 86 + 22 + 4 + 20
    }

    return Math.max(col1, col2)
}

const toLocaleString = function (value: number): string {
    const replaceAll = function (str: string, search: string, replacement: string): string {
        return str.replace(new RegExp(search, 'g'), replacement)
    }

    return replaceAll(value.toLocaleString(), ',', '.')
}

const CollectionsTable = ({
    collections,
    collectionsNames,
    readonly,
    deleteTable,
    deleteIndex,
    loading,
    searchText,
    selectedFilter,
    hideTrivial,
    saveIndex,
    decrementHeight
}: CollectionTableProps): JSX.Element => {
    const [deleteTargetCollection, setDeleteTargetCollection] = useState<null | string>(null)
    const [createIndexTargetCollection, setCreateIndexTargetCollection] = useState<null | MappingCollectionType>(null)
    const [showCreateIndex, setShowCreateIndex] = useState<boolean>(false)
    const [windowWidth, windowHeight] = useWindowResize()
    const listRef = useRef<null | any>()

    const sizeMap = useRef({})
    const setSize = useCallback((index, size) => {
        sizeMap.current = { ...sizeMap.current, [index]: size }
        if (listRef != null && listRef.current != null) {
            listRef.current.resetAfterIndex(index)
        }
    }, [])
    const getSize = (index: number): number => sizeMap.current[index] || sizeManager(index, collections)

    const tableWidth = windowWidth - 272 <= 1008 ? 1008 : windowWidth - 272
    const tableHeight = windowHeight - 300 - decrementHeight

    const collectionsFilter = collections
        .filter(collection => {
            if (searchText === '') return true

            return collection.name.includes(searchText) || collection.type?.includes(searchText)
        })
        .filter(
            collection =>
                selectedFilter === CollectionFilterEnum.all ||
                (!collection.partialResult &&
                    selectedFilter === CollectionFilterEnum.withoutTimestamp &&
                    !collection.indexes.some(isTimestampIndex)) ||
                (!collection.partialResult &&
                    selectedFilter === CollectionFilterEnum.withCustom &&
                    collection.indexes.some(idx => !isTimestampIndex(idx) && !isIdIndex(idx)))
        )
        .map(collection => {
            return !hideTrivial
                ? collection
                : {
                    ...collection,
                    indexes: collection.indexes.filter(idx => !isTimestampIndex(idx) && !isIdIndex(idx))
                }
        })

    const showCreateIndexCollectionModal = (name: string, type: string | null): void => {
        setCreateIndexTargetCollection({ name, type })
        setShowCreateIndex(true)
    }

    const closeCreateIndexModal = (): void => {
        setCreateIndexTargetCollection(null)
        setShowCreateIndex(false)
    }

    return (
        <>
            {loading && <Loading className="loading" />}

            {!collectionsFilter || !collectionsFilter.length ? (
                !loading && (
                    <div className="is-empty">
                        {i18n('No collections')} {selectedFilter !== 'all' ? i18n(selectedFilter) : null}
                    </div>
                )
            ) : (
                <Table className="collections-table table-fixed">
                    <thead>
                        <tr>
                            <th className="collection">{i18n('Collection')}</th>
                            <th className="type">{i18n('Event Type')}</th>
                            <th className="index">{i18n('Index')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <List
                            ref={listRef}
                            className="virtualizer"
                            height={tableHeight}
                            width={tableWidth + 27}
                            itemCount={collectionsFilter.length}
                            itemSize={getSize}
                            itemData={collectionsFilter}
                        >
                            {({ data, index, style }) => (
                                <div style={style}>
                                    <Row
                                        collection={data[index]}
                                        index={index}
                                        setSize={setSize}
                                        maxWidth={tableWidth}
                                        windowWidth={tableWidth}
                                        readonly={readonly}
                                        showDeleteModal={setDeleteTargetCollection}
                                        setShowCreateIndexModal={showCreateIndexCollectionModal}
                                        deleteIndex={deleteIndex}
                                    />
                                </div>
                            )}
                        </List>

                        {showCreateIndex && (
                            <ModalCreateIndex
                                show={showCreateIndex}
                                onHide={closeCreateIndexModal}
                                collections={collectionsNames}
                                preSelectedCollections={
                                    createIndexTargetCollection ? [createIndexTargetCollection] : []
                                }
                                saveIndex={saveIndex}
                            />
                        )}

                        <ActionConfirmationInputDialog
                            show={deleteTargetCollection != null}
                            onHide={() => setDeleteTargetCollection(null)}
                            className="confirmation-input-modal modal-error modal-sm"
                            confirmationText={i18n('Delete')}
                            content={
                                <CollectionModalAlertContainer className="modal-confirmation-alert">
                                    <p className="mongodb-modal-title">
                                        {i18n('Are you sure you want to delete the collection')}
                                        {`"`}
                                        <b>{deleteTargetCollection}</b>
                                        {`"`} ?
                                    </p>
                                    <strong className="msg-error">{i18n('This action cannot be undone')}.</strong>
                                    <p>{i18n('All data on this collection will be lost')}.</p>
                                    <p>
                                        <span>{i18n('If you really want to delete the collection, type')} </span>
                                        <span className="remove-text">{i18n('Delete')} </span>
                                        <span>{i18n('in the field below before completing the action')}:</span>
                                    </p>
                                </CollectionModalAlertContainer>
                            }
                            btnText={i18n('Delete collection')}
                            apply={deleteTable(deleteTargetCollection)}
                        />
                    </tbody>
                </Table>
            )}
        </>
    )
}

interface RowProps {
    collection: Collection
    index: number
    windowWidth: number
    readonly: boolean
    maxWidth: number
    setSize: (index: number, size: number) => void
    showDeleteModal: (name: string) => void
    setShowCreateIndexModal: (name: string, type: string | null) => void
    deleteIndex: (selectedIndex: Index) => void
}

const Row = ({
    collection,
    index,
    setSize,
    windowWidth,
    readonly,
    maxWidth,
    showDeleteModal,
    setShowCreateIndexModal,
    deleteIndex
}: RowProps): JSX.Element => {
    const rowRef = useRef<null | HTMLDivElement>()

    useEffect(() => {
        if (rowRef.current && rowRef.current.getBoundingClientRect) {
            setSize(index, rowRef.current.getBoundingClientRect().height + 20)
        }
    }, [setSize, index, windowWidth])

    const {
        name,
        type = '',
        stats = null,
        indexes = [],
        trivialIndexes = 0,
        customIndexes = 0,
        createdIn,
        partialResult
    } = collection
    const fullName = name.split('.')
    const sizeOnDisk = stats && stats.bytesUsed ? bytesToSize(stats.bytesUsed) : 0
    const count = stats && stats.count ? toLocaleString(stats.count) : 0
    const totalIndexes = trivialIndexes + customIndexes

    return (
        <div ref={rowRef} style={{ width: maxWidth }}>
            <tr key={name}>
                <td className="collection">
                    <div>
                        <div>
                            <p className="title">
                                {isLiveCollection(name) ? (
                                    isRegularCollectionName(name) ? (
                                        <>
                                            <span className="preffix">{fullName[0]}.</span>
                                            <span>{fullName[1]}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="preffix">
                                                {fullName[0]}.{fullName[1]}.
                                            </span>
                                            <span>{fullName[2]}</span>
                                        </>
                                    )
                                ) : (
                                    <span>{name}</span>
                                )}
                            </p>
                            {!readonly && (
                                <a className="btn-delete" onClick={() => showDeleteModal(name)}>
                                    <i className="far fa-trash-alt fa-fw" /> {i18n('Delete collection')}
                                </a>
                            )}
                        </div>
                        <>
                            <ul className="collection-info">
                                <li>
                                    <p>{i18n('Size on disk')}</p>
                                    {partialResult ? (
                                        <p className="value loading-simple">
                                            <SimpleLoading />
                                        </p>
                                    ) : (
                                        <p className="value">{sizeOnDisk}</p>
                                    )}
                                </li>
                                <li>
                                    <p>{i18n('Documents')}</p>
                                    {partialResult ? (
                                        <p className="value loading-simple">
                                            <SimpleLoading />
                                        </p>
                                    ) : (
                                        <p className="value">{count}</p>
                                    )}
                                </li>
                            </ul>
                        </>
                    </div>
                </td>
                <td className="type">
                    <p>{type}</p>
                </td>
                <td className="index">
                    <CollectionsLoadControllers>
                        <p className="item">
                            {!partialResult ? (
                                <>
                                    <span>{i18n('Latest updated: ')}</span>
                                    {LiveDateService.formatDateTime(createdIn)}
                                </>
                            ) : (
                                i18n('Gathering collection stats...')
                            )}
                        </p>
                    </CollectionsLoadControllers>
                    {partialResult ? (
                        <div className="loading-index">
                            <SimpleLoading />
                        </div>
                    ) : (
                        indexes && indexes.length ? (
                            <div className="table-container">
                                <IndexesTable
                                    indexes={indexes as Index[]}
                                    readonly={readonly}
                                    deleteIndex={deleteIndex}
                                />
                            </div>
                        ) : (
                            <p className="no-index">
                                {totalIndexes === 0
                                    ? i18n('No index found on this collection')
                                    : customIndexes === 0 &&
                                    trivialIndexes > 0 &&
                                    i18n('No custom indexes. %{trivialIndexes} default indexes hidden.', trivialIndexes)}
                            </p>
                        )
                    )}
                    {!partialResult && !readonly && (
                        <a className="btn btn-create-index" onClick={() => setShowCreateIndexModal(name, type)}>
                            <i className="fa fa-plus-circle fa-fw" /> {i18n('Create index here')}
                        </a>
                    )}
                </td>
            </tr>
        </div>
    )
}

export default CollectionsTable

const CollectionsLoadControllers = styled.div`
    display: flex;
    justify-content: flex-end;
    height: 18px;

    > p {
        border-bottom: 1px solid var(--bg-body);
        margin: 0px;
        font-family: $font-regular;
        font-size: 13px;
        text-align: right;
        line-height: 1.625rem;
        color: var(--gray-800);
    }

    > p.item {
        font-size: 11px;

        > span {
            font-size: 11px;
            text-transform: uppercase;
            color: var(--gray);
        }
    }
`

const CollectionModalAlertContainer = styled.div`
    p.mongodb-modal-title {
        word-break: break-all;
    }
`
